import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import { PhotoViewer, type PhotoViewerItem } from '@indipia/ui-core'
import { fetchShipPhotoBlob, getShipPhotos } from '../../api/shipApi'
import { SHIP_TEXT, SHIP_TOAST, shipErrorMessage, shipPopupPhotoAlt, type ShipPhoto } from '../../constants/ship.logic'


interface Props {
  orderVendorId: number
  title: string
  /** 진입점이 가리킨 종류(상차/하차 글자 링크). 없으면 0번부터. */
  startKind: 'LOAD' | 'UNLOAD' | null
  onClose: () => void
  /** 첫 사진을 받을 때까지 true — 행 버튼 loading 용. */
  onLoadingChange: (loading: boolean) => void
}

/**
 * 사진 확대 호스트 — 사진 목록(C4a) 조회 후 원본(C4b, blob)을 objectURL 로 바꿔 ui-core PhotoViewer 에 전달한다.
 * 시작 사진 1장을 받은 뒤에 뷰어를 열고 나머지는 열린 뒤 순차로 받는다. 실패한 사진은 items 에서 빼고 토스트 1회.
 * 닫거나 언마운트되면 objectURL 전부 revoke.
 */
export function ShipPhotoViewerHost({ orderVendorId, title, startKind, onClose, onLoadingChange }: Props) {
  const listQ = useQuery<ShipPhoto[]>({
    queryKey: ['ship', 'photos', orderVendorId],
    queryFn: async () => getShipPhotos(orderVendorId),
    staleTime: 0,
    retry: false,
  })

  const [urls, setUrls] = useState<Record<number, string>>({})
  const [failed, setFailed] = useState<ReadonlySet<number>>(new Set())
  const [currentId, setCurrentId] = useState<number | null>(null)
  const created = useRef<string[]>([])
  const onLoadingRef = useRef(onLoadingChange)
  onLoadingRef.current = onLoadingChange
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  const photos = listQ.data

  // 목록 조회 실패·0장
  useEffect(() => {
    if (listQ.isError) {
      toast.error(shipErrorMessage(listQ.error).text)
      onLoadingRef.current(false)
      onCloseRef.current()
    } else if (photos && photos.length === 0) {
      toast.error(SHIP_TOAST.photoNone)
      onLoadingRef.current(false)
      onCloseRef.current()
    }
  }, [listQ.isError, listQ.error, photos])

  // 시작 사진 먼저, 나머지 순차
  useEffect(() => {
    if (!photos || photos.length === 0) return
    let cancelled = false
    const startIdx = Math.max(0, startKind ? photos.findIndex(p => p.photoTypeCode === startKind) : 0)
    const order = [photos[startIdx], ...photos.filter((_, i) => i !== startIdx)]
    ;(async () => {
      let failCount = 0
      for (let i = 0; i < order.length; i++) {
        const p = order[i]
        try {
          const blob = await fetchShipPhotoBlob(orderVendorId, p.orderPhotoId, 'original')
          if (cancelled) return
          const url = URL.createObjectURL(blob)
          created.current.push(url)
          setUrls(prev => ({ ...prev, [p.orderPhotoId]: url }))
          if (i === 0) { setCurrentId(p.orderPhotoId); onLoadingRef.current(false) }
        } catch {
          if (cancelled) return
          failCount += 1
          setFailed(prev => new Set(prev).add(p.orderPhotoId))
          if (i === 0) {
            // 시작 사진이 실패하면 다음으로 받은 사진이 현재가 되도록 둔다.
            onLoadingRef.current(false)
          }
        }
      }
      if (!cancelled && failCount > 0) {
        toast.error(failCount === order.length ? SHIP_TEXT.photoLoadFailed : SHIP_TOAST.photoPartial)
        if (failCount === order.length) onCloseRef.current()
      }
    })()
    return () => { cancelled = true }
  }, [photos, startKind, orderVendorId])

  useEffect(() => () => {
    created.current.forEach(u => URL.revokeObjectURL(u))
    created.current = []
    onLoadingRef.current(false)
  }, [])

  const items: PhotoViewerItem[] = useMemo(() => {
    if (!photos) return []
    const seqByKind: Record<'LOAD' | 'UNLOAD', number> = { LOAD: 0, UNLOAD: 0 }
    const out: PhotoViewerItem[] = []
    for (const p of photos) {
      seqByKind[p.photoTypeCode] += 1
      const src = urls[p.orderPhotoId]
      if (!src || failed.has(p.orderPhotoId)) continue
      out.push({ id: p.orderPhotoId, src, alt: shipPopupPhotoAlt(p.photoTypeCode, seqByKind[p.photoTypeCode]) })
    }
    return out
  }, [photos, urls, failed])

  if (items.length === 0) return null
  const found = items.findIndex(it => it.id === currentId)
  const index = found >= 0 ? found : 0

  return (
    <PhotoViewer
      open
      onClose={onClose}
      items={items}
      index={index}
      onIndexChange={i => setCurrentId((items[i]?.id as number | undefined) ?? null)}
      title={title}
    />
  )
}
